import {useEffect, useState} from "react";
import {ContactItem} from "../types/contacts";
import {contactsService} from "../services/contacts-service";
import {useForm} from "antd/es/form/Form";
import {openSuccessNotification, showErrorNotification} from "../helpers/mainHelpers";
import {Modal} from "antd";
import {DeleteOutlined} from "@ant-design/icons";

const contactTypeOptions = [
  {label: 'Телефон', value: "phone"},
  {label: 'Пошта', value: "email"},
  {label: 'Посилання', value: "link"},
  {label: 'Інше', value: "other"},
]

export const useContactsController = () => {
  const [contactsList, setContactsList] = useState<ContactItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedContactItem, setSelectedContactItem] = useState<ContactItem | undefined>(undefined)
  const [lastUpdate, setLastUpdate] = useState(0)
  const [form] = useForm()

  useEffect(() => {
    if (!isModalOpen) {
      setSelectedContactItem(undefined)
    }
  }, [isModalOpen])

  useEffect(() => {
    if (selectedContactItem) {
      form.setFieldsValue(selectedContactItem)
    } else {
      form.resetFields()
    }
  }, [selectedContactItem])

  useEffect(() => {
    fetchContactsList([])
  }, [lastUpdate])

  const updateContactItem = async (data: ContactItem) => {
    setIsLoading(true)
    try {
      await contactsService.update({$id: selectedContactItem?.$id, ...data})
      toggleModal()
      toggleLastUpdate()
      openSuccessNotification("Дані успішно оновлено!")
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка при оновленні запису!", description: JSON.stringify(error)})
    } finally {
      setIsLoading(false)
    }
  }

  const deleteContactItem = async (id: string) => {
    setIsLoading(true)
    try {
      await contactsService.delete(id)
      const updatedList = contactsList.filter(item => item.$id !== id)
      setContactsList(updatedList)
      openSuccessNotification("Запис успішно видалено!")
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка видалення запису!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const confirmDelete = (props: { id: string }) => {
    const {confirm} = Modal;
    confirm(
      {
        width: 400,
        icon: <DeleteOutlined/>,
        title: `Видалити запис?`,
        okText: "Видалити",
        okButtonProps: {danger: true},
        cancelText: "Cкасувати",
        onOk: async () => {
          await deleteContactItem(props.id)
        },
      });
  }

  const toggleLastUpdate = () => {
    setLastUpdate(Date.now())
  }

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen)
  }


  const fetchContactsList = async (queries: string[]) => {
    setIsLoading(true)
    try {
      const list = await contactsService.list(queries)
      const sortedByOrderData = list.sort((a, b) => a.order - b.order)
      setContactsList(sortedByOrderData)
    } catch (error) {
      showErrorNotification({message: "Помилка отримання контакної інформації!"})
    } finally {
      setIsLoading(false)
    }
  }

  const createContactItem = async (data: ContactItem) => {
    setIsLoading(true)
    try {
      await contactsService.create(data)
      openSuccessNotification("Запис успішно створено!")
      toggleModal()
      toggleLastUpdate()
    } catch (error) {
      showErrorNotification({message: "Помилка при створенні запису!", description: JSON.stringify(error)})
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const submitButtonHandler = async (data: ContactItem) => {
    if (selectedContactItem) {
      await updateContactItem(data)
    } else {
      await createContactItem(data)
    }
  }

  const editButtonHandler = (data: ContactItem) => {
    setSelectedContactItem(data)
    toggleModal()
  }


  return {
    contactsList,
    isLoading,
    fetchContactsList,
    toggleModal,
    selectedContactItem,
    isModalOpen,
    form,
    contactTypeOptions,
    submitButtonHandler,
    confirmDelete,
    editButtonHandler
  }
}

export default useContactsController